import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  AlertCircle,
  Clock,
  Send,
  RefreshCw,
  Package,
  Users,
  ListChecks,
  ChevronDown,
  ChevronUp,
  XCircle,
  History,
} from 'lucide-react';
import { toast } from 'react-toastify';
import projectAssetService from '../../../../services/projectAssetService';
import { equipmentService } from '../../../../services/equipmentService';
import projectService from '../../../../services/projectService';
import amsProjectService from '../../../../services/amsProjectService';
import { getProjectApprovalDetails, getProjectApprovalHistory, type ProjectApprovalHistory, type ApprovalHistoryEntry } from '../../../../services/projectApprovalService';
import {
  getProjectPendingChanges,
  submitProjectPendingChanges,
} from '../../../../services/postStartChangeService';
import { subscribeToProjectProgressUpdates } from '../../utils/projectProgressEvents';
import RightDrawer from '../../../../shared/components/form/RightDrawer';
import { useAuth } from '../../../../context/AuthContext';

interface ProjectProgressHeaderProps {
  project: any;
  onRefresh?: () => void;
  refreshSignal?: number;
  isAms?: boolean; // Flag to indicate if this is an AMS project
}

interface ProgressItem {
  id: string;
  label: string;
  status: 'completed' | 'pending' | 'rejected';
  count?: number;
  total?: number;
  suffix?: string;
  tooltip?: string;
  icon: React.ReactNode;
  rejectedItems?: string[];
}

/** Rejected review items for one AMS audit, shown grouped under the audit's name. */
interface AmsRejectedGroup {
  auditLabel: string;
  items: { label: string; comment?: string }[];
}

const formatAuditFrequency = (f?: string): string => {
  const labels: Record<string, string> = {
    yearly: 'Yearly', halfYearly: 'Half Yearly', quarterly: 'Quarterly',
    monthly: 'Monthly', weekly: 'Weekly', daily: 'Daily',
  };
  return (f && labels[f]) || f || '';
};

const formatDate = (d: string) => {
  try {
    const date = new Date(d);
    return date.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return d;
  }
};

const HistoryEntryItem: React.FC<{ entry: ApprovalHistoryEntry }> = ({ entry }) => {
  const typeConfig = {
    submitted: { color: 'text-blue-600 bg-blue-50 border-blue-200', icon: Send, label: 'Sent for review' },
    resubmitted: { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: RefreshCw, label: 'Resubmitted' },
    rejected: { color: 'text-red-600 bg-red-50 border-red-200', icon: XCircle, label: 'Rejected' },
    approved: { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle2, label: 'Approved' },
  };
  const config = typeConfig[entry.type];
  const Icon = config.icon;
  return (
    <div className={`rounded-lg border p-3 ${config.color}`}>
      <div className="flex items-start gap-2">
        <Icon size={16} className="flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{config.label}</span>
            <span className="text-xs opacity-80">v{entry.version}</span>
            {entry.by && <span className="text-xs">by {entry.by}</span>}
          </div>
          <div className="text-xs mt-1 opacity-90">{formatDate(entry.date)}</div>
          {entry.reason && <p className="text-sm mt-2">{entry.reason}</p>}
          {entry.notes && <p className="text-sm mt-1 italic">{entry.notes}</p>}
          {entry.summary && (
            <div className="text-xs mt-1 flex gap-2 flex-wrap">
              <span>P: {entry.summary.pending}</span>
              <span>A: {entry.summary.approved}</span>
              <span>R: {entry.summary.rejected}</span>
              <span>M: {entry.summary.modified}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface RemainingRequirements {
  equipmentWithoutResources: string[];
  rejectedAssets: string[];
  rejectedEquipment: string[];
  rejectedCheckpoints: string[];
  rejectedResources: string[];
}

const ProjectProgressHeader: React.FC<ProjectProgressHeaderProps> = ({ project, onRefresh, refreshSignal, isAms = false }) => {
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitComment, setResubmitComment] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [headActionLoading, setHeadActionLoading] = useState(false);
  const [amsRejectedGroups, setAmsRejectedGroups] = useState<AmsRejectedGroup[]>([]);
  const [remainingRequirements, setRemainingRequirements] = useState<RemainingRequirements>({
    equipmentWithoutResources: [],
    rejectedAssets: [],
    rejectedEquipment: [],
    rejectedCheckpoints: [],
    rejectedResources: []
  });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [resubmissionCount, setResubmissionCount] = useState<number>(0);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [approvalHistory, setApprovalHistory] = useState<ProjectApprovalHistory | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Total of asset/checkpoint/equipment edits made after the head approved
  // the project. While > 0, the "Submit Changes for Review" button replaces
  // the inert "Project approved" banner so the manager can surface the
  // batch to the head's pending queue without rewinding approvalStatus.
  const [pendingChangesTotal, setPendingChangesTotal] = useState<number>(0);
  const [pendingChangesSubmitting, setPendingChangesSubmitting] = useState<boolean>(false);
  const [showSubmitChangesModal, setShowSubmitChangesModal] = useState<boolean>(false);
  const [submitChangesComment, setSubmitChangesComment] = useState<string>('');

  const { user } = useAuth();
  // Can approve/reject: only Head and Admin. Manager (requester) must NOT see approve/reject.
  const canApproveReject = useMemo(() => {
    const roleName = (user?.role?.name || '').toLowerCase();
    const roleType = (user?.role?.roleType || '').toLowerCase();
    if (roleType === 'requester') return false;
    return roleName.includes('head') || roleName.includes('admin') || roleType === 'admin';
  }, [user]);

  const projectId = project._id || project.id;
  const approvalStatusRaw = project.approvalStatus;
  const approvalStatus = typeof approvalStatusRaw === 'string' ? approvalStatusRaw.toLowerCase().trim() : (approvalStatusRaw ?? '');
  const isRejected = approvalStatus === 'rejected';
  const isApproved = ['approved', 'in_progress'].includes(approvalStatus);
  // Already sent for review / resubmitted — waiting for Head to approve or reject
  const isPendingApproval = ['pending_approval', 'in_review', 'under_review'].includes(approvalStatus);

  // AMS approves at the audit level, not the project level — hide the project-level
  // submit/resubmit buttons entirely for AMS. OMS/EPC keep the existing behavior.
  const showApprovalButtons = isAms ? false : true;

  // Can show "Send for Review" only when draft/not_required (first time) AND not already pending
  const canSubmit = ['draft', 'not_required'].includes(approvalStatus) && !isPendingApproval && showApprovalButtons;

  // Resubmit only applies after a rejection — it should NOT show when:
  //   - the project is in draft (use Send for Review instead),
  //   - the project is approved (nothing to resubmit),
  //   - the project is currently pending approval (waiting for Head's decision).
  // Previously this also returned true for any Head user, which surfaced Resubmit during draft and
  // alongside Send for Review. Restricting it to `isRejected` keeps the two flows mutually exclusive.
  // For AMS projects the buttons are additionally role-gated (requester only).
  const canResubmit = isRejected && !isPendingApproval && showApprovalButtons;

  // Show history icon when project has been sent for review at least once
  const hasApprovalHistory = isRejected || isPendingApproval || isApproved;

  const fetchProgressData = useCallback(async () => {
    // AMS projects don't live in the OMS project-assets / equipment collections, so the
    // OMS progress endpoints (project-assets/with-checkpoints) 500 for them. Build the
    // equivalent progress/rejection picture from AMS audit approvals instead — this
    // drives `hasRejected`/`totalRejectedCount` below, which previously stayed false
    // for every AMS project (nothing was ever fetched), making a real audit-section
    // rejection look like "all clear" in this header.
    if (isAms) {
      try {
        setLoading(true);
        const { getAuditApprovals } = await import('../../../../services/amsAuditApprovalService');
        const amsCheckpointService = (await import('../../../../services/amsAuditCheckpointService')).default;
        const res = await getAuditApprovals({ projectId, department: 'AMS', limit: 100 });
        const approvals = res?.docs || [];

        const sectionLabels: Record<string, string> = {
          auditType: 'Audit info',
          auditors: 'Auditors',
          assetsAndEquipment: 'Assets & equipment',
        };

        // The approval subdocs only carry a checkpoint id, not its name. For any audit with a
        // rejected checkpoint, fetch that audit's checkpoints once and map id -> name, so the
        // requester sees WHICH checkpoint to fix instead of a generic "Checkpoint".
        const auditsNeedingNames = approvals.filter((ap: any) =>
          [...(ap.checkpointApprovals?.projectCheckpoints || []), ...(ap.checkpointApprovals?.assetCheckpoints || [])]
            .some((cp: any) => cp.status === 'rejected'));
        const cpNameByAudit = new Map<string, Map<string, string>>();
        await Promise.all(auditsNeedingNames.map(async (ap: any) => {
          const auditId = String(ap.amsProjectAuditId);
          try {
            const [proj, asset] = await Promise.all([
              amsCheckpointService.getAuditCheckpoints(auditId, 'project'),
              amsCheckpointService.getAuditCheckpoints(auditId, 'asset'),
            ]);
            const m = new Map<string, string>();
            [...(proj || []), ...(asset || [])].forEach((c: any) => m.set(String(c._id), c.name));
            cpNameByAudit.set(auditId, m);
          } catch {
            /* names are best-effort; fall back to "Checkpoint" */
          }
        }));

        const rejectedFlat: string[] = [];
        const rejectedGroups: AmsRejectedGroup[] = [];
        let totalSections = 0;
        let approvedSections = 0;
        let rejectedSectionsCount = 0;

        approvals.forEach((ap: any) => {
          const auditId = String(ap.amsProjectAuditId);
          const auditName = ap.audit?.auditTypeId?.name || 'Audit';
          const freq = formatAuditFrequency(ap.audit?.auditFrequency);
          const auditLabel = freq ? `${auditName} — ${freq}` : auditName;
          const nameMap = cpNameByAudit.get(auditId);
          const groupItems: { label: string; comment?: string }[] = [];

          (['auditType', 'auditors', 'assetsAndEquipment'] as const).forEach((key) => {
            const section = ap.sections?.[key];
            if (!section || section.status === 'not_required') return;
            totalSections++;
            if (section.status === 'approved') approvedSections++;
            if (section.status === 'rejected') {
              rejectedSectionsCount++;
              groupItems.push({ label: sectionLabels[key], comment: section.approverComments });
              rejectedFlat.push(`${sectionLabels[key]}${section.approverComments ? `: ${section.approverComments}` : ''}`);
            }
          });
          [...(ap.checkpointApprovals?.projectCheckpoints || []), ...(ap.checkpointApprovals?.assetCheckpoints || [])].forEach((cp: any) => {
            if (cp.status === 'not_required') return;
            totalSections++;
            if (cp.status === 'approved') approvedSections++;
            if (cp.status === 'rejected') {
              rejectedSectionsCount++;
              const cpName = nameMap?.get(String(cp.checkpointId)) || 'Checkpoint';
              groupItems.push({ label: cpName, comment: cp.approverComments });
              rejectedFlat.push(`${cpName}${cp.approverComments ? `: ${cp.approverComments}` : ''}`);
            }
          });

          if (groupItems.length > 0) rejectedGroups.push({ auditLabel, items: groupItems });
        });

        const pendingCount = Math.max(0, totalSections - approvedSections - rejectedSectionsCount);
        const items: ProgressItem[] = approvals.length > 0 ? [
          {
            id: 'audit-sections',
            label: 'Audit Review',
            status: rejectedSectionsCount > 0 ? 'rejected' : (approvedSections === totalSections && totalSections > 0 ? 'completed' : 'pending'),
            count: approvedSections,
            total: totalSections,
            suffix: 'approved',
            tooltip: `${approvedSections} of ${totalSections} audit review items approved · ${rejectedSectionsCount} rejected · ${pendingCount} pending`,
            icon: <ListChecks size={16} />,
            rejectedItems: rejectedFlat,
          },
        ] : [];

        setAmsRejectedGroups(rejectedGroups);
        setRemainingRequirements({
          equipmentWithoutResources: [],
          rejectedAssets: [],
          rejectedEquipment: [],
          rejectedCheckpoints: rejectedFlat,
          rejectedResources: [],
        });
        setProgressItems(items);
      } catch (error) {
        console.error('[ProjectProgress] Failed to fetch AMS audit approvals:', error);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      setLoading(true);

      console.log('[ProjectProgress] Fetching progress data for project:', projectId);

      // Fetch project assets with checkpoint approval counts (align with asset-level rejected/updated logic)
      const assets = await projectAssetService.getProjectAssetsWithDetails(projectId);
      const assetsList = Array.isArray(assets) ? assets : [];
      console.log('[ProjectProgress] Fetched assets:', assetsList.length, assetsList);

      // Also fetch all equipment for the project as a fallback/verification
      let allProjectEquipment: any[] = [];
      try {
        const projectEquipment = await equipmentService.getByProject(projectId);
        allProjectEquipment = Array.isArray(projectEquipment) ? projectEquipment : ((projectEquipment as any)?.data || []);
        console.log('[ProjectProgress] Fetched all project equipment:', allProjectEquipment.length);
      } catch (error) {
        console.error('[ProjectProgress] Failed to fetch project equipment:', error);
      }

      const normalizeId = (value: any): string | undefined => {
        if (!value) return undefined;
        if (typeof value === 'string') return value;
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'object') {
          if (value._id) return value._id.toString();
          if (value.id) return value.id.toString();
          if (value.$oid) return value.$oid.toString();
          try {
            return value.toString();
          } catch {
            return undefined;
          }
        }
        return undefined;
      };

      const normalizeNameKey = (value?: string) => value?.trim().toLowerCase() || undefined;

      const addEquipmentToMap = (map: Map<string, any[]>, key: string | undefined, equipmentItem: any) => {
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(equipmentItem);
      };

      // Create richer equipment lookup maps so we can handle inconsistent references
      const equipmentByProjectAssetId = new Map<string, any[]>();
      const equipmentByMasterAssetId = new Map<string, any[]>();
      const equipmentByAssetName = new Map<string, any[]>();

      allProjectEquipment.forEach((eq: any) => {
        const projectAssetKey = normalizeId(eq.projectAssetId?._id || eq.projectAssetId);
        const masterAssetKey = normalizeId(
          eq.masterAssetId?._id ||
          eq.masterAssetId ||
          eq.assetId?._id ||
          eq.assetId
        );
        const assetNameKey = normalizeNameKey(
          eq.assetName ||
          eq.projectAssetId?.name ||
          eq.masterAssetId?.name ||
          eq.assetId?.name ||
          eq.asset?.name
        );

        addEquipmentToMap(equipmentByProjectAssetId, projectAssetKey, eq);
        addEquipmentToMap(equipmentByMasterAssetId, masterAssetKey, eq);
        addEquipmentToMap(equipmentByAssetName, assetNameKey, eq);
      });

      console.log('[ProjectProgress] Equipment map summary:', {
        byProjectAsset: equipmentByProjectAssetId.size,
        byMasterAsset: equipmentByMasterAssetId.size,
        byAssetName: equipmentByAssetName.size
      });

      // Fetch equipment for each asset
      let totalEquipment = 0;
      let assignedEquipment = 0;
      let rejectedAssets: string[] = [];
      let rejectedEquipment: string[] = [];
      let rejectedCheckpoints: string[] = [];
      let rejectedResources: string[] = [];
      let equipmentWithoutResources: string[] = [];

      // Track unique resource IDs assigned to equipment
      const assignedResourceIds = new Set<string>();

      // Process each asset
      for (const asset of assetsList) {
        const assetId = normalizeId(asset._id || (asset as any).id);
        console.log(`[ProjectProgress] Processing asset: ${asset.name} (${assetId})`);

        // Check asset approval status (backend returns this but interface doesn't define it)
        const assetWithStatus = asset as any;
        if (assetWithStatus.approvalStatus === 'rejected') {
          rejectedAssets.push(asset.name);
        }

        const masterAssetRef = (asset as any).masterAssetId;
        const masterAssetId = normalizeId(
          masterAssetRef?._id ||
          masterAssetRef?.id ||
          masterAssetRef
        );
        const assetNameKey = normalizeNameKey(asset.name || masterAssetRef?.name);

        // Resolve equipment for this asset using the lookup maps
        let equipment: any[] = [];
        if (assetId && equipmentByProjectAssetId.has(assetId)) {
          equipment = equipmentByProjectAssetId.get(assetId)!;
        } else if (masterAssetId && equipmentByMasterAssetId.has(masterAssetId)) {
          equipment = equipmentByMasterAssetId.get(masterAssetId)!;
        } else if (assetNameKey && equipmentByAssetName.has(assetNameKey)) {
          equipment = equipmentByAssetName.get(assetNameKey)!;
        }
        console.log(`[ProjectProgress] Asset ${asset.name}: Resolved ${equipment.length} equipment items`);

        totalEquipment += equipment.length;
        console.log(`[ProjectProgress] Asset ${asset.name}: Total equipment so far: ${totalEquipment}`);

        // Check each equipment for assignment and approval status
        equipment.forEach((eq: any) => {
          // Check if equipment is assigned to a resource
          const assignedTo = eq.assignedTo || eq.siteEngineerId || eq.siteSupervisorId || eq.engineerId;
          if (assignedTo) {
            assignedEquipment++;
            // Track unique resource IDs for resource count
            const resourceId = assignedTo._id?.toString() || assignedTo.toString() || assignedTo.id?.toString();
            if (resourceId) {
              assignedResourceIds.add(resourceId);
            }
          } else {
            const eqName = eq.equipmentName || eq.name || eq.equipmentCode || 'Unnamed Equipment';
            equipmentWithoutResources.push(`${asset.name}: ${eqName}`);
          }

          // Check equipment approval status
          if (eq.approvalStatus === 'rejected') {
            const eqName = eq.equipmentName || eq.name || eq.equipmentCode || 'Unnamed Equipment';
            rejectedEquipment.push(`${asset.name}: ${eqName}`);
          }
        });

        // Rejected checkpoints only (approvalStatus === 'rejected'); modified = user updated, so not rejected
        try {
          const assetAny = asset as any;
          let checkpoints: any[] = [];
          if (assetAny.checkpoints?.projectLevel?.length || assetAny.checkpoints?.master?.length) {
            checkpoints = [...(assetAny.checkpoints.projectLevel || []), ...(assetAny.checkpoints.master || [])];
          }
          if (checkpoints.length === 0 && asset._id) {
            try {
              checkpoints = await projectAssetService.getProjectAssetCheckpoints(asset._id);
            } catch (err) {
              // Checkpoints might not exist yet
            }
          }
          checkpoints.forEach((checkpoint: any) => {
            if (checkpoint.approvalStatus === 'rejected') {
              rejectedCheckpoints.push(`${asset.name}: ${checkpoint.name}`);
            }
          });
        } catch (error) {
          // Error fetching checkpoints - skip
        }
      }

      // Resource count is already calculated from equipment assignments above
      // If there are no assets/equipment, resource assignment count should be 0
      const resourceCount = totalEquipment > 0 ? assignedResourceIds.size : 0;

      console.log('[ProjectProgress] Summary:', {
        totalAssets: assetsList.length,
        totalEquipment,
        assignedEquipment,
        resourceCount,
        equipmentWithoutResources: equipmentWithoutResources.length
      });

      // Get project approval details to check for rejected resources
      try {
        const approvalDetails = await getProjectApprovalDetails(projectId);

        // Store resubmission count for Resubmit button
        const count = approvalDetails?.overview?.resubmissionCount ?? 0;
        setResubmissionCount(count);

        // Check resources from hierarchy and independent engineers
        if (approvalDetails?.resources) {
          const allResources = [
            ...(approvalDetails.resources.hierarchy || []),
            ...(approvalDetails.resources.independentEngineers || [])
          ];

          allResources.forEach((resource: any) => {
            if (resource.approvalStatus === 'rejected') {
              const roleName = resource.role?.name || resource.role || 'Resource';
              const resourceName = resource.name || resource.email || resource.userId || 'Unknown';
              rejectedResources.push(`${roleName}: ${resourceName}`);
            }
          });
        }

        // Also check assets for rejected checkpoints from approval details
        if (approvalDetails?.assets) {
          approvalDetails.assets.forEach((assetDetail: any) => {
            // Check project level checkpoints
            if (assetDetail.checkpoints?.projectLevelCheckpoints) {
              assetDetail.checkpoints.projectLevelCheckpoints.forEach((cp: any) => {
                if (cp.approvalStatus === 'rejected') {
                  const existing = rejectedCheckpoints.find(c => c.includes(cp.name));
                  if (!existing) {
                    rejectedCheckpoints.push(`${assetDetail.assetName}: ${cp.name}`);
                  }
                }
              });
            }
          });
        }
      } catch (error) {
        // Approval details might not be available yet
        console.log('Could not fetch approval details:', error);
      }

      const items: ProgressItem[] = [
        {
          id: 'assets',
          label: 'Assets Added',
          status: assetsList.length > 0 ? (rejectedAssets.length > 0 ? 'rejected' : 'completed') : 'pending',
          count: assetsList.length,
          icon: <Package size={16} />,
          rejectedItems: rejectedAssets
        },
        {
          id: 'equipment',
          label: 'Equipment Added',
          status: totalEquipment > 0 ? (rejectedEquipment.length > 0 ? 'rejected' : 'completed') : 'pending',
          count: totalEquipment,
          icon: <ListChecks size={16} />,
          rejectedItems: rejectedEquipment
        },
        {
          id: 'assignments',
          label: 'Resource Assignment',
          // Resource assignment is only relevant if there are equipment
          // If no equipment, it should be pending (not completed)
          status: totalEquipment > 0
            ? (resourceCount > 0 ? (rejectedResources.length > 0 ? 'rejected' : 'completed') : 'pending')
            : 'pending',
          count: resourceCount,
          icon: <Users size={16} />,
          rejectedItems: rejectedResources
        }
      ];

      if (rejectedCheckpoints.length > 0) {
        items.push({
          id: 'checkpoints',
          label: 'Checkpoints',
          status: 'rejected',
          count: rejectedCheckpoints.length,
          icon: <AlertCircle size={16} />,
          rejectedItems: rejectedCheckpoints
        });
      }

      // Store detailed requirements for display
      setRemainingRequirements({
        equipmentWithoutResources,
        rejectedAssets,
        rejectedEquipment,
        rejectedCheckpoints,
        rejectedResources
      });

      setProgressItems(items);
    } catch (error) {
      console.error('Failed to fetch progress data:', error);
    } finally {
      setLoading(false);
    }
  }, [projectId, project, isAms]);

  useEffect(() => {
    if (projectId) {
      fetchProgressData();
    }
  }, [projectId, refreshSignal, fetchProgressData]);

  // Pending post-start changes (edits made after head approval). Drives the
  // "Submit Changes for Review" button visibility for approved projects.
  // Kept independent from `fetchProgressData` so a failure here can't break
  // the main progress display.
  const fetchPendingChanges = useCallback(async () => {
    // Post-start changes are an OMS approval concept; AMS projects have no
    // pending-changes endpoint, so keep the count at 0 (button never renders).
    if (!projectId || isAms) return;
    try {
      const res = await getProjectPendingChanges(projectId);
      setPendingChangesTotal(res?.data?.total ?? 0);
    } catch (err) {
      // Don't surface to user — pending-changes count is auxiliary. Log only.
      console.warn('[ProjectProgress] failed to fetch pending changes:', err);
      setPendingChangesTotal(0);
    }
  }, [projectId, isAms]);

  useEffect(() => {
    if (projectId) fetchPendingChanges();
  }, [projectId, refreshSignal, fetchPendingChanges]);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    const unsubscribe = subscribeToProjectProgressUpdates((detail) => {
      if (detail.projectId === projectId) {
        fetchProgressData();
        fetchPendingChanges();
      }
    });

    return () => {
      unsubscribe?.();
    };
  }, [projectId, fetchProgressData, fetchPendingChanges]);

  const handleSubmitForReview = async () => {
    if (!canApproveReject) {
      if (remainingRequirements.equipmentWithoutResources.length > 0) {
        toast.error(`Cannot submit: ${remainingRequirements.equipmentWithoutResources.length} equipment need resource assignment`);
        return;
      }
    }

    try {
      setSubmitting(true);
      // AMS projects live in a different collection; the OMS submit-for-approval
      // endpoint can't find them. Use the AMS project-level submit-to-manager flow.
      if (isAms) {
        const res = await amsProjectService.submitProjectApprovalToManager(projectId);
        const audits = res?.data?.auditsSubmitted ?? 0;
        toast.success(
          audits > 0
            ? `Project submitted for review (${audits} audit${audits === 1 ? '' : 's'} sent to the head)`
            : 'Project submitted for review successfully'
        );
      } else {
        await projectService.submitProjectForApproval(projectId);
        toast.success('Project submitted for review successfully');
      }
      onRefresh?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit project for review');
    } finally {
      setSubmitting(false);
    }
  };

  const handleHeadApprove = async () => {
    if (!projectId) return;
    try {
      setHeadActionLoading(true);
      await projectService.approveProject(projectId, { comments: 'Project approved by Head.' });
      toast.success('Project approved. Manager will be notified.');
      setShowRejectModal(false);
      setRejectReason('');
      onRefresh?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve project');
    } finally {
      setHeadActionLoading(false);
    }
  };

  const handleHeadReject = async () => {
    if (!rejectReason.trim() || rejectReason.trim().length < 10) {
      toast.error('Please provide a rejection reason (minimum 10 characters).');
      return;
    }
    if (!projectId) return;
    try {
      setHeadActionLoading(true);
      await projectService.rejectProject(projectId, { reason: rejectReason.trim() });
      toast.success('Project rejected. Manager will be notified.');
      setShowRejectModal(false);
      setRejectReason('');
      onRefresh?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to reject project');
    } finally {
      setHeadActionLoading(false);
    }
  };

  const handleResubmit = async () => {
    if (resubmitComment.trim().length < 10) {
      toast.error('Please provide a detailed comment (minimum 10 characters)');
      return;
    }

    // Check if there are still rejected items (checkpoints, resources, equipment, assets)
    const hasRejectedCheckpoints = remainingRequirements.rejectedCheckpoints.length > 0;
    const hasRejectedResources = remainingRequirements.rejectedResources.length > 0;
    const hasRejectedEquipment = remainingRequirements.rejectedEquipment.length > 0;
    const hasRejectedAssets = remainingRequirements.rejectedAssets.length > 0;
    const hasAnyRejections = hasRejectedCheckpoints || hasRejectedResources || hasRejectedEquipment || hasRejectedAssets;

    // Check if comment explains why no changes are needed
    const commentLower = resubmitComment.toLowerCase();
    const explainsNoChange = commentLower.includes('no change') ||
      commentLower.includes('no modification') ||
      commentLower.includes('as is') ||
      commentLower.includes('unchanged');

    // Warn if there are rejections AND no explanation, but allow approvers to proceed without confirmation
    if (hasAnyRejections && !explainsNoChange && !canApproveReject) {
      const rejectionTypes: string[] = [];
      if (hasRejectedCheckpoints) rejectionTypes.push(`${remainingRequirements.rejectedCheckpoints.length} checkpoint(s)`);
      if (hasRejectedResources) rejectionTypes.push(`${remainingRequirements.rejectedResources.length} resource(s)`);
      if (hasRejectedEquipment) rejectionTypes.push(`${remainingRequirements.rejectedEquipment.length} equipment`);
      if (hasRejectedAssets) rejectionTypes.push(`${remainingRequirements.rejectedAssets.length} asset(s)`);

      const confirmMessage = `You have rejected items (${rejectionTypes.join(', ')}). Are you sure you want to resubmit without fixing them?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    // If user claims no changes needed, ensure they provide proper justification
    if (explainsNoChange && resubmitComment.trim().length < 30) {
      toast.error('If no changes are needed, please provide a detailed explanation (minimum 30 characters)');
      return;
    }

    try {
      setSubmitting(true);
      // AMS projects live in a different collection; the OMS submit-for-approval
      // endpoint returns "Project not found". Use the AMS project-level submit-to-
      // manager flow, which also clears the prior rejection on (re)submit.
      if (isAms) {
        const res = await amsProjectService.submitProjectApprovalToManager(projectId, { comments: resubmitComment });
        const audits = res?.data?.auditsSubmitted ?? 0;
        toast.success(
          audits > 0
            ? `Project resubmitted for review (${audits} audit${audits === 1 ? '' : 's'} sent to the head)`
            : 'Project resubmitted for review successfully'
        );
      } else {
        await projectService.submitProjectForApproval(projectId, {
          comments: resubmitComment
        });
        toast.success('Project resubmitted for review successfully');
      }
      setShowResubmitModal(false);
      setResubmitComment('');
      onRefresh?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to resubmit project');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit post-start changes for re-approval. Used when the project is
  // already approved and the manager has edited assets / checkpoints / added
  // equipment afterward. Backend creates an Approval record so the head's
  // pending queue picks it up; Project.approvalStatus is intentionally NOT
  // changed (the project stays 'approved' / 'in_progress').
  const handleSubmitPendingChanges = async () => {
    if (!projectId) return;
    if (pendingChangesTotal <= 0) {
      toast.error('No pending changes to submit.');
      return;
    }
    const trimmed = submitChangesComment.trim();
    try {
      setPendingChangesSubmitting(true);
      await submitProjectPendingChanges(projectId, trimmed || undefined);
      toast.success(
        `Submitted ${pendingChangesTotal} pending change${pendingChangesTotal === 1 ? '' : 's'} for review.`
      );
      setShowSubmitChangesModal(false);
      setSubmitChangesComment('');
      // Don't zero out optimistically — wait for the head to act. Refresh in
      // case other progress fields shifted.
      onRefresh?.();
      fetchPendingChanges();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to submit pending changes');
    } finally {
      setPendingChangesSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected': return 'text-red-600 bg-red-50 border-red-200';
      case 'pending': return 'text-amber-600 bg-amber-50 border-amber-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle2 size={16} className="text-green-600" />;
      case 'rejected': return <AlertCircle size={16} className="text-red-600" />;
      case 'pending': return <Clock size={16} className="text-amber-600" />;
      default: return <Clock size={16} className="text-gray-600" />;
    }
  };

  const hasRejected = progressItems.some(item => item.status === 'rejected');
  const hasPending = progressItems.some(p => p.status === 'pending');
  // Show progress bar (title + pills) only when rejected or warning (pending); hide when all success (completed)
  const showProgressBar = hasRejected || hasPending;
  const totalMissing = remainingRequirements.equipmentWithoutResources.length;
  // Block Send for Review when equipment exists but is not assigned to a resource
  const blockingMissing = remainingRequirements.equipmentWithoutResources.length;
  // AMS submit-to-manager requires a project location (backend BRD 13.1). Without it the
  // submit 400s, so surface it as a precondition instead of a silent failure.
  const amsLocationMissing = isAms && !(project?.locationId || project?.location);
  const totalRejectedCount =
    remainingRequirements.rejectedAssets.length +
    remainingRequirements.rejectedEquipment.length +
    remainingRequirements.rejectedCheckpoints.length +
    remainingRequirements.rejectedResources.length;

  // Show progress header only when there is a warning (project not started or not complete).
  // `pendingChangesTotal > 0` also keeps the header rendered so the manager can see
  // the "Submit Changes for Review" affordance on an otherwise-approved project.
  const hasWarning = hasRejected || totalMissing > 0 || isRejected || isPendingApproval ||
    pendingChangesTotal > 0 ||
    progressItems.some(p => p.status === 'pending');
  if (!loading && !hasWarning && isApproved) {
    return null;
  }

  return (
    <div className="bg-white border-b border-gray-200/80 shadow-sm transition-all duration-300 relative overflow-hidden">
      {/* Prime accent bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-600 via-indigo-500 to-violet-600" aria-hidden />
      <div className="px-3 py-2 relative">
        <div className="flex items-center justify-between gap-3">
          {/* Left: Title & Progress Pills - only when rejected or warning; hidden when all success */}
          {showProgressBar && (
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-800 whitespace-nowrap tracking-tight">Project Progress</h3>
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar">
                {progressItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.06 }}
                    title={item.tooltip || undefined}
                    className={`flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium whitespace-nowrap tabular-nums [&>svg]:w-3 [&>svg]:h-3 [&>svg]:shrink-0 ${getStatusColor(item.status)}`}
                  >
                    {item.icon}
                    <span>
                      {item.label}
                      {item.count !== undefined && item.total !== undefined && ` ${item.count}/${item.total}`}
                      {item.count !== undefined && item.total === undefined && ` ${item.count}`}
                      {item.suffix ? ` ${item.suffix}` : ''}
                    </span>
                    {item.rejectedItems && item.rejectedItems.length > 0 && (
                      <span className="ml-0.5 px-1 py-px bg-red-100 text-red-700 rounded text-[9px] font-semibold">
                        {item.rejectedItems.length}
                      </span>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Right: Actions - compact, right-aligned */}
          <div className="flex items-center justify-end gap-1.5 flex-shrink-0 ml-auto">
            {hasApprovalHistory && (
              <button
                onClick={async () => {
                  setShowHistoryDrawer(true);
                  setHistoryLoading(true);
                  try {
                    const history = await getProjectApprovalHistory(projectId);
                    setApprovalHistory(history);
                  } catch (err) {
                    toast.error('Failed to load approval history');
                    setApprovalHistory(null);
                  } finally {
                    setHistoryLoading(false);
                  }
                }}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Approval History"
              >
                <History size={14} />
              </button>
            )}
            <button
              onClick={fetchProgressData}
              disabled={loading}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Refresh Progress"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            {/* Status tag: Approved | Rejected | Waiting for approval */}
            {isApproved && (
              <span
                className="flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded text-[11px] font-medium"
                title="Project approved"
              >
                <CheckCircle2 size={12} />
                <span>Approved</span>
              </span>
            )}
            {isRejected && !isApproved && (
              <span
                className="flex items-center gap-1 px-2.5 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-[11px] font-medium"
                title="Project rejected"
              >
                <XCircle size={12} />
                <span>Rejected</span>
              </span>
            )}
            {isPendingApproval && !canApproveReject && (
              <span
                className="flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[11px] font-medium"
                title="Project has been sent for review. Wait for Head to approve or reject."
              >
                <Clock size={12} />
                <span>Waiting for approval</span>
              </span>
            )}
            {canApproveReject && isPendingApproval && !isAms && (
              <>
                <button
                  onClick={handleHeadApprove}
                  disabled={headActionLoading}
                  className="flex items-center gap-1 px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50 shadow-sm"
                  title="Approve project and notify manager"
                >
                  <CheckCircle2 size={12} />
                  <span>{headActionLoading ? 'Processing…' : 'Approve'}</span>
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={headActionLoading}
                  className="flex items-center gap-1 px-2.5 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50 shadow-sm"
                  title="Reject project and notify manager"
                >
                  <XCircle size={12} />
                  <span>Reject</span>
                </button>
              </>
            )}
            {canResubmit && (
              <button
                onClick={() => setShowResubmitModal(true)}
                disabled={submitting || amsLocationMissing}
                title={amsLocationMissing ? 'Add a project location (Edit → Location) before resubmitting' : undefined}
                className="flex items-center gap-1 px-2.5 py-1 bg-orange-600 hover:bg-orange-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <RefreshCw size={12} />
                <span>{isAms ? 'Resubmit Project' : 'Resubmit'}{resubmissionCount > 0 ? ` (${resubmissionCount})` : ''}</span>
              </button>
            )}
            {canSubmit && !isRejected && (
              <button
                onClick={handleSubmitForReview}
                disabled={submitting || blockingMissing > 0 || amsLocationMissing}
                title={amsLocationMissing ? 'Add a project location (Edit → Location) before submitting' : undefined}
                className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Send size={12} />
                <span>{isAms ? 'Submit to Manager' : 'Send for Review'}</span>
              </button>
            )}
          </div>
        </div>

        {/* Pending approval: Manager (requester) sees waiting; Head sees action hint */}
        {isPendingApproval && !canApproveReject && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-1.5 flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] bg-blue-50 text-blue-800 border border-blue-200"
          >
            <Clock size={14} className="text-blue-600 flex-shrink-0" />
            <span className="font-medium">Sent for review — waiting for approval.</span>
            <span className="text-blue-600/90">Resubmit and Send for Review are disabled until Head responds.</span>
          </motion.div>
        )}
        {isPendingApproval && canApproveReject && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-1.5 flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] bg-amber-50 text-amber-800 border border-amber-200"
          >
            <AlertCircle size={14} className="text-amber-600 flex-shrink-0" />
            <span className="font-medium">Project pending your decision.</span>
            <span className="text-amber-700/90">Use Approve to notify manager, or Reject with reason.</span>
          </motion.div>
        )}

        {/* Compact Status Bar (Warnings/Errors) - Red only when there are still rejected items; green when addressed */}
        {(hasRejected || totalMissing > 0 || isRejected) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className={`mt-1.5 flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] border ${
              isRejected && !hasRejected
                ? 'bg-green-50 text-green-800 border-green-200'
                : isRejected || hasRejected
                  ? 'bg-red-50 text-red-800 border-red-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200'
            }`}
          >
            <div className="flex items-center gap-1.5 overflow-hidden min-w-0">
              {isRejected && !hasRejected ? (
                <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
              ) : isRejected || hasRejected ? (
                <AlertCircle size={14} className="text-red-600 flex-shrink-0" />
              ) : (
                <AlertCircle size={14} className="text-amber-600 flex-shrink-0" />
              )}
              <div className="flex items-center gap-1.5 truncate min-w-0">
                <span className="font-semibold truncate">
                  {isRejected && !hasRejected
                    ? 'Rejections addressed'
                    : isRejected
                      ? 'Project Rejected'
                      : hasRejected
                        ? 'Action Required'
                        : 'Pending Requirements'}
                </span>
                <span className="hidden sm:inline text-opacity-90 truncate">
                  {isRejected && !hasRejected && '— Resubmit for review'}
                  {isRejected && hasRejected && project.rejectionReason && `— ${project.rejectionReason}`}
                  {!isRejected && (
                    <>
                      {hasRejected && `· ${totalRejectedCount} rejected`}
                      {totalMissing > 0 && ` · ${totalMissing} missing`}
                    </>
                  )}
                </span>
              </div>
            </div>
            <button
              onClick={() => setDetailsOpen(!detailsOpen)}
              className={`flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-wider hover:underline flex-shrink-0 ml-1.5 ${
                isRejected && !hasRejected ? 'text-green-700' : isRejected || hasRejected ? 'text-red-700' : 'text-amber-700'
              }`}
            >
              {detailsOpen ? 'Hide' : 'Details'}
              {detailsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </motion.div>
        )}

        {/* Expandable Detailed Guidance - compact */}
        {detailsOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="mt-1.5 space-y-1.5 border-t border-gray-100 pt-1.5"
          >
            {isRejected && !hasRejected && (
              <div className="px-2 py-1.5 bg-green-50 rounded border border-green-200 text-[11px] text-green-800">
                <span className="font-semibold">All rejected items have been updated.</span> Resubmit for review when ready.
              </div>
            )}
            {isRejected && hasRejected && project.rejectionReason && (
              <div className="px-2 py-1.5 bg-red-50 rounded border border-red-100 text-[11px] text-red-800">
                <span className="font-semibold">Rejection:</span> {project.rejectionReason}
              </div>
            )}
            {remainingRequirements.equipmentWithoutResources.length > 0 && (
              <div className="px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px]">
                <div className="font-semibold text-amber-900 mb-0.5">
                  {remainingRequirements.equipmentWithoutResources.length} equipment need assignment
                </div>
                <ul className="text-amber-800 list-disc list-inside pl-1 max-h-16 overflow-y-auto">
                  {remainingRequirements.equipmentWithoutResources.map((eq, i) => (
                    <li key={i}>{eq}</li>
                  ))}
                </ul>
              </div>
            )}
            {hasRejected && (
              <div className="px-2.5 py-2 bg-red-50 border border-red-200 rounded text-xs">
                <div className="font-semibold text-red-900 mb-1">
                  Rejected items ({totalRejectedCount}) — fix these, then resend for approval
                </div>
                {isAms && amsRejectedGroups.length > 0 ? (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {amsRejectedGroups.map((g, gi) => (
                      <div key={gi}>
                        <div className="font-semibold text-red-800">{g.auditLabel}</div>
                        <ul className="mt-0.5 space-y-0.5 pl-1">
                          {g.items.map((it, ii) => (
                            <li key={ii} className="flex gap-1.5 text-red-700">
                              <span className="text-red-400 leading-5">•</span>
                              <span>
                                <span className="font-medium">{it.label}</span>
                                {it.comment ? <span className="text-red-600"> — “{it.comment}”</span> : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {remainingRequirements.rejectedAssets.length > 0 && (
                      <div>
                        <span className="font-medium text-red-800">Assets:</span>
                        <div className="pl-1 text-red-700">{remainingRequirements.rejectedAssets.join(', ')}</div>
                      </div>
                    )}
                    {remainingRequirements.rejectedEquipment.length > 0 && (
                      <div>
                        <span className="font-medium text-red-800">Equipment:</span>
                        <div className="pl-1 text-red-700">{remainingRequirements.rejectedEquipment.join(', ')}</div>
                      </div>
                    )}
                    {remainingRequirements.rejectedCheckpoints.length > 0 && (
                      <div>
                        <span className="font-medium text-red-800">{isAms ? 'Audit review:' : 'Checkpoints:'}</span>
                        <div className="pl-1 text-red-700 max-h-12 overflow-y-auto">
                          {remainingRequirements.rejectedCheckpoints.map((cp, i) => <div key={i}>{cp}</div>)}
                        </div>
                      </div>
                    )}
                    {remainingRequirements.rejectedResources.length > 0 && (
                      <div>
                        <span className="font-medium text-red-800">Resources:</span>
                        <div className="pl-1 text-red-700">{remainingRequirements.rejectedResources.join(', ')}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* AMS: location is a hard submit precondition — surface it instead of a silent 400 */}
        {amsLocationMissing && !isPendingApproval && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 px-2.5 py-1 rounded border border-amber-200">
            <AlertCircle size={12} className="text-amber-600" />
            <span className="font-medium">Project location is required before you can submit for review.</span>
            <span className="text-amber-700/90">Add it in Edit → Location.</span>
          </div>
        )}

        {/* Success / Approved - compact */}
        {!hasRejected && !isRejected && totalMissing === 0 && !amsLocationMissing && progressItems.length > 0 && !isApproved && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-green-700 bg-green-50 px-2.5 py-1 rounded border border-green-200">
            <CheckCircle2 size={12} />
            <span className="font-medium">All requirements met. Ready for review.</span>
          </div>
        )}
        {isApproved && pendingChangesTotal === 0 && (
          <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">
            <CheckCircle2 size={12} />
            <span className="font-medium">Project approved. Changes require re-approval.</span>
          </div>
        )}
        {/* Approved + post-start changes pending head review: actionable
            banner so the manager can surface the batch to the head's queue.
            Only the requester (not the head themselves) should submit. */}
        {isApproved && pendingChangesTotal > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-[11px] text-amber-800 bg-amber-50 px-2.5 py-1.5 rounded border border-amber-200">
            <div className="flex items-center gap-1.5 min-w-0">
              <AlertCircle size={14} className="text-amber-600 flex-shrink-0" />
              <span className="font-semibold">
                {pendingChangesTotal} change{pendingChangesTotal === 1 ? '' : 's'} pending re-approval
              </span>
              <span className="text-amber-700/90 truncate">
                — assets / checkpoints / equipment edited after head approval are excluded from task generation until reviewed.
              </span>
            </div>
            {!canApproveReject && (
              <button
                onClick={() => setShowSubmitChangesModal(true)}
                disabled={pendingChangesSubmitting}
                className="flex items-center gap-1 px-2.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[11px] font-semibold transition-colors disabled:opacity-50 shadow-sm flex-shrink-0"
                title="Send these changes to the project head for review"
              >
                <Send size={12} />
                <span>Submit Changes for Review</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Resubmit Drawer */}
      <RightDrawer
        open={showResubmitModal}
        onClose={() => setShowResubmitModal(false)}
        title="Resubmit Project for Review"
        size="md"
      >
        <div className="p-6 space-y-6">
          {/* Show rejected items if any */}
          {progressItems.some(item => item.status === 'rejected') && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="text-sm font-medium text-red-800 mb-2">Rejected Items:</h4>
              <ul className="text-sm text-red-700 space-y-1">
                {progressItems
                  .filter(item => item.status === 'rejected')
                  .map(item => (
                    <li key={item.id} className="flex items-center gap-2">
                      <AlertCircle size={12} />
                      <span>{item.label}</span>
                      {item.rejectedItems && item.rejectedItems.length > 0 && (
                        <span className="text-xs">({item.rejectedItems.length} items)</span>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment (Required) - Minimum 10 characters
            </label>
            <textarea
              value={resubmitComment}
              onChange={(e) => setResubmitComment(e.target.value)}
              placeholder="Explain what changes were made to address the rejection. If no changes are needed, provide detailed justification (minimum 20 characters)."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={6}
            />
            <div className="mt-1 flex justify-between text-xs text-gray-500">
              <span>
                {resubmitComment.trim().length < 10 ?
                  `Need ${10 - resubmitComment.trim().length} more characters` :
                  'Minimum length met'
                }
              </span>
              <span>{resubmitComment.length}/500</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => setShowResubmitModal(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleResubmit}
              disabled={submitting || resubmitComment.trim().length < 10}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
            >
              {submitting && <RefreshCw size={16} className="animate-spin" />}
              <span>Resubmit</span>
            </button>
          </div>
        </div>
      </RightDrawer>

      {/* Submit Pending Changes Drawer (post-start changes on approved project) */}
      <RightDrawer
        open={showSubmitChangesModal}
        onClose={() => setShowSubmitChangesModal(false)}
        title="Submit Changes for Re-Approval"
        size="md"
      >
        <div className="p-6 space-y-6">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <p className="font-medium">
              {pendingChangesTotal} pending change{pendingChangesTotal === 1 ? '' : 's'} will be sent to the project head for review.
            </p>
            <p className="text-xs text-amber-800 mt-1">
              The project stays approved — only the edited asset(s) / checkpoint(s) / equipment item(s) remain excluded from task generation until the head reviews them.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comment (optional)
            </label>
            <textarea
              value={submitChangesComment}
              onChange={(e) => setSubmitChangesComment(e.target.value)}
              placeholder="Briefly describe what you changed and why (helps the head review faster)."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              rows={5}
              maxLength={500}
            />
            <div className="mt-1 flex justify-end text-xs text-gray-500">
              <span>{submitChangesComment.length}/500</span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => setShowSubmitChangesModal(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitPendingChanges}
              disabled={pendingChangesSubmitting || pendingChangesTotal <= 0}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
            >
              {pendingChangesSubmitting && <RefreshCw size={16} className="animate-spin" />}
              <Send size={14} />
              <span>Submit for Review</span>
            </button>
          </div>
        </div>
      </RightDrawer>

      {/* Approval History Drawer */}
      <RightDrawer
        open={showHistoryDrawer}
        onClose={() => {
          setShowHistoryDrawer(false);
          setApprovalHistory(null);
        }}
        title="Approval History"
        size="md"
      >
        <div className="p-6">
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-gray-400" />
            </div>
          ) : approvalHistory && approvalHistory.history.length > 0 ? (
            <div className="space-y-4">
              <div className="flex gap-2 text-sm text-gray-600 mb-4">
                <span className="font-medium">Total submissions:</span>
                <span>{approvalHistory.totalSubmissions}</span>
                {approvalHistory.resubmissionCount > 0 && (
                  <>
                    <span className="text-gray-400">|</span>
                    <span className="font-medium">Resubmissions:</span>
                    <span>{approvalHistory.resubmissionCount}</span>
                  </>
                )}
              </div>
              <div className="border-l-2 border-gray-200 pl-4 space-y-4">
                {approvalHistory.history.map((entry: ApprovalHistoryEntry) => (
                  <HistoryEntryItem key={entry.version} entry={entry} />
                ))}
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500 text-sm">
              No approval history yet.
            </div>
          )}
        </div>
      </RightDrawer>

      {/* Head: Reject Project Drawer */}
      <RightDrawer
        open={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        title="Reject Project"
        size="md"
      >
        <div className="p-6 space-y-6">
          <p className="text-sm text-gray-600">
            Provide a clear reason for rejection. The project manager will be notified and can address the feedback before resubmitting.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rejection reason (Required) - Minimum 10 characters
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why this project is being rejected..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
              rows={6}
            />
            <div className="mt-1 flex justify-between text-xs text-gray-500">
              <span>
                {rejectReason.trim().length < 10
                  ? `Need ${10 - rejectReason.trim().length} more characters`
                  : 'Minimum length met'}
              </span>
              <span>{rejectReason.length}/500</span>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setShowRejectModal(false);
                setRejectReason('');
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleHeadReject}
              disabled={headActionLoading || rejectReason.trim().length < 10}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 shadow-sm"
            >
              {headActionLoading && <RefreshCw size={16} className="animate-spin" />}
              <span>{headActionLoading ? 'Rejecting…' : 'Reject & Notify Manager'}</span>
            </button>
          </div>
        </div>
      </RightDrawer>
    </div>
  );
};

export default ProjectProgressHeader;
